import { useId, useMemo, useState, type ReactNode } from "react";
import classNames from "classnames";
import {
    LiveRegion,
    type Announcer,
} from "@design-system/LiveRegion/LiveRegion";
import { styleWithVars } from "@lib/cssVars";
import type {
    BoardCardShortcut,
    BoardColumnDef,
    BoardLaneDef,
    BoardPlacement,
} from "./Board.types";
import type { BoardColumnAddingResolver } from "./boardAdd";
import type { BoardKeyboardCopy } from "./boardAnnouncements";
import { BOARD_SCROLLER_PROPS } from "./boardAttributes";
import { BoardKeyboardHelp } from "./BoardKeyboardHelp";
import { BoardRovingContext } from "./boardRoving";
import { getCellEntries, type BoardCells } from "./boardCells";
import { getColumnTemplate, type BoardCollapse } from "./boardCollapse";
import { BoardLaneRows, type BoardLayoutCell } from "./BoardLaneRows";
import { BoardScrollColumn } from "./BoardScrollColumn";
import { BoardScrollEdges } from "./BoardScrollEdges";
import { getWindowedCellKeys, isCellWindowed } from "./boardWindowing";
import {
    countLeadingStrips,
    useBoardColumnChrome,
    type BoardColumnRefusal,
    type BoardStripRenderProps,
} from "./useBoardColumnChrome";
import { useBoardFocusRestore } from "./useBoardFocusRestore";
import { useBoardLayoutFocus } from "./useBoardLayoutFocus";
import { useBoardSelectionRoot } from "./useBoardSelection";
import { useHeightProperty } from "./useHeightProperty";
import { BoardScrollerContext } from "./useBoardWindow";
import { useScrollingAttribute } from "./useScrollingAttribute";
import styles from "./Board.module.scss";
import laneStyles from "./BoardLanes.module.scss";

interface BoardLayoutProps<TItem> {
    "aria-label": string;
    columns: readonly BoardColumnDef[];
    lanes?: readonly BoardLaneDef[];
    /** A command island floats over the bottom edge: each scroller ends with room for it. */
    hasCommandIsland?: boolean;
    cells: BoardCells<TItem>;
    collapse: BoardCollapse;
    /** Some cell shows a gap `+` above its first card: every cell makes room. */
    hasGapAboveFirst: boolean;
    getColumnRefusal?: (columnId: string) => BoardColumnRefusal | undefined;
    /** Also gives the header `+`, shown only on boards without lanes. */
    getColumnAdding: BoardColumnAddingResolver<TItem>;
    renderCell: (cell: BoardLayoutCell) => ReactNode;
    renderCollapsedColumn: (props: BoardStripRenderProps) => ReactNode;
    /**
     * Lanes kept mounted on a windowed board: where the dragged card is. The
     * focused card's lane is kept too, as is the card itself.
     */
    pinnedLaneIds?: readonly string[];
    /**
     * A card is being dragged or settling: sibling copies can be outlined,
     * and arrow keys belong to the drag.
     */
    isDragActive?: boolean;
    /** The board's live region, shared with drag announcements. */
    announcer: Announcer;
    keyboardCopy: BoardKeyboardCopy;
    cardShortcuts: readonly BoardCardShortcut[];
    /** Read on mount: the item whose first card takes focus once it mounts. */
    initialFocusedItemId?: string;
}

const NO_LANE_IDS: readonly string[] = [];

/**
 * Board grid. One track per column, so lanes (rows of cells on a shared
 * `subgrid`) stay aligned with the column headers. Without lanes each
 * column scrolls on its own; with lanes the whole board scrolls.
 */
export const BoardLayout = <TItem,>({
    "aria-label": ariaLabel,
    columns,
    lanes,
    hasCommandIsland = false,
    cells,
    collapse,
    hasGapAboveFirst,
    getColumnRefusal,
    getColumnAdding,
    renderCell,
    renderCollapsedColumn,
    pinnedLaneIds = NO_LANE_IDS,
    isDragActive = false,
    announcer,
    keyboardCopy,
    cardShortcuts,
    initialFocusedItemId,
}: Readonly<BoardLayoutProps<TItem>>) => {
    const baseId = useId();
    const [frame, setFrame] = useState<HTMLElement | null>(null);
    const [boardScroller, setBoardScroller] = useState<HTMLElement | null>(
        null,
    );
    const headerRowRef = useHeightProperty(frame, "--board-header-row-height");
    const laneHeaderRef = useHeightProperty(
        frame,
        "--board-lane-header-height",
    );
    useScrollingAttribute(boardScroller);
    const boardScrollerContext = useMemo(
        () => ({ element: boardScroller, isShared: true }),
        [boardScroller],
    );
    const instructionsId = `${baseId}keyboard`;
    const {
        focusedKey,
        clearFocusedKey,
        lanePins,
        rovingContext,
        sectionHandlers,
        isHintShown,
    } = useBoardLayoutFocus({
        cells,
        columns,
        lanes,
        collapse,
        getColumnAdding,
        announce: announcer.announce,
        canAdd: keyboardCopy.canAdd,
        isDragActive,
        pinnedLaneIds,
        instructionsId,
        cardShortcuts,
        initialFocusedItemId,
    });
    const getCellCount = (placement: BoardPlacement) =>
        getCellEntries(cells, placement).length;
    const hasLanes = lanes !== undefined;
    const windowedCellKeys = useMemo(
        () => getWindowedCellKeys({ cells, columns, lanes }),
        [cells, columns, lanes],
    );
    const isLaneWindowed = hasLanes && windowedCellKeys.length > 0;
    const selectionRoot = useBoardSelectionRoot({
        isDragActive,
        onKeyDown: sectionHandlers.onKeyDown,
    });
    useBoardFocusRestore({
        root: boardScroller,
        focusedKey,
        cells,
        windowedCellKeys,
        onGiveUp: clearFocusedKey,
    });
    const getColumnTitleId = (index: number) => `${baseId}column${index}`;
    const { renderHeader, renderStrip, isCollapsed } = useBoardColumnChrome({
        cells,
        collapse,
        hasLanes,
        getColumnTitleId,
        getColumnRefusal,
        getColumnAdding,
        renderCollapsedColumn,
    });

    return (
        <BoardRovingContext.Provider value={rovingContext}>
            <div
                ref={setFrame}
                className={classNames(styles.frame, {
                    [laneStyles.lanesFrame]: hasLanes,
                    [styles.hasCommandIsland]: hasCommandIsland,
                })}
            >
                <section
                    ref={setBoardScroller}
                    aria-label={ariaLabel}
                    className={classNames(styles.board, {
                        [laneStyles.hasLanes]: hasLanes,
                    })}
                    data-gap-above-first={hasGapAboveFirst}
                    data-drag-active={isDragActive}
                    {...(hasLanes ? BOARD_SCROLLER_PROPS : undefined)}
                    {...sectionHandlers}
                    {...selectionRoot.handlers}
                    style={styleWithVars({
                        gridTemplateColumns: getColumnTemplate(
                            columns,
                            collapse,
                        ),
                        "--board-leading-strips": hasLanes
                            ? countLeadingStrips(columns, collapse)
                            : undefined,
                    })}
                >
                    {hasLanes ? (
                        <BoardScrollerContext.Provider
                            value={boardScrollerContext}
                        >
                            <BoardLaneRows
                                columns={columns}
                                lanes={lanes}
                                laneCounts={cells.laneCounts}
                                collapse={collapse}
                                getCellCount={getCellCount}
                                laneWindowing={
                                    isLaneWindowed
                                        ? { pinnedLaneIds: lanePins }
                                        : undefined
                                }
                                idPrefix={baseId}
                                getColumnTitleId={getColumnTitleId}
                                renderHeader={renderHeader}
                                renderCell={renderCell}
                                headerRowRef={headerRowRef}
                                laneHeaderRef={laneHeaderRef}
                                strips={columns.map(
                                    (column, index) =>
                                        isCollapsed(column) &&
                                        renderStrip(column, index),
                                )}
                            />
                        </BoardScrollerContext.Provider>
                    ) : (
                        columns.map((column, index) =>
                            isCollapsed(column) ? (
                                renderStrip(column, index)
                            ) : (
                                <BoardScrollColumn
                                    key={column.id}
                                    labelledBy={getColumnTitleId(index)}
                                    style={{ gridColumn: index + 1 }}
                                    header={renderHeader(column, index)}
                                >
                                    {renderCell({
                                        column,
                                        lane: null,
                                        placement: {
                                            columnId: column.id,
                                            laneId: null,
                                        },
                                        labelledBy: getColumnTitleId(index),
                                        isWindowed: isCellWindowed(cells, {
                                            columnId: column.id,
                                            laneId: null,
                                        }),
                                    })}
                                </BoardScrollColumn>
                            ),
                        )
                    )}
                </section>
                <BoardScrollEdges
                    scroller={boardScroller}
                    axis={hasLanes ? "both" : "x"}
                    className={classNames(styles.boardEdges, {
                        [laneStyles.laneEdges]: hasLanes,
                    })}
                />
                <BoardKeyboardHelp
                    instructionsId={instructionsId}
                    copy={keyboardCopy}
                    isHintShown={isHintShown}
                />
                {selectionRoot.describedById !== null && (
                    <div id={selectionRoot.describedById} hidden>
                        Selected
                    </div>
                )}
                <LiveRegion announcement={announcer.announcement} />
            </div>
        </BoardRovingContext.Provider>
    );
};
